import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Typography,
  DatePicker,
  Select,
  InputNumber,
  Tag
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../../api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const ProductionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const statusMap = {
    pending: { text: '待生产', color: 'orange' },
    in_progress: { text: '生产中', color: 'blue' },
    completed: { text: '已完成', color: 'green' },
    cancelled: { text: '已取消', color: 'red' },
  };

  const fetchPlans = useCallback(async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const response = await api.get('/v1/production_plans', {
        params: { page, pageSize, search },
      });
      setPlans(response.data.data.map(p => ({ ...p, key: p.id })));
      setPagination({ current: page, pageSize, total: response.data.total });
    } catch (error) {
      message.error('获取生产计划列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get('/v1/products', { params: { limit: -1 } });
      setProducts(response.data.data);
    } catch (error) {
      message.error('获取产品列表失败');
    }
  }, []);

  useEffect(() => {
    fetchPlans(pagination.current, pagination.pageSize);
    fetchProducts();
  }, [fetchPlans, fetchProducts]);

  const handleTableChange = (newPagination) => {
    fetchPlans(newPagination.current, newPagination.pageSize);
  };

  const handleSearch = (value) => {
    fetchPlans(1, pagination.pageSize, value);
  };

  const showModal = (record = null) => {
    setEditingRecord(record);
    if (record) {
      form.setFieldsValue({
        ...record,
        plan_start_date: dayjs(record.plan_start_date),
        plan_end_date: dayjs(record.plan_end_date),
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const isViewMode = editingRecord?.status === 'completed';

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        plan_start_date: values.plan_start_date.format('YYYY-MM-DD'),
        plan_end_date: values.plan_end_date.format('YYYY-MM-DD'),
      };

      if (editingRecord) {
        await api.put(`/v1/production_plans/${editingRecord.id}`, payload);
        message.success('生产计划更新成功');
      } else {
        await api.post('/v1/production_plans', payload);
        message.success('生产计划创建成功');
      }
      handleCancel();
      fetchPlans(pagination.current, pagination.pageSize);
    } catch (error) {
      const errorMsg = error.response?.data?.error || '操作失败';
      message.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/production_plans/${id}`);
      message.success('生产计划删除成功');
      fetchPlans(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    { title: '计划单号', dataIndex: 'plan_no', key: 'plan_no' },
    { title: '产品名称', dataIndex: ['product', 'name'], key: 'product_name' },
    { title: '计划数量（包装）', dataIndex: 'quantity', key: 'quantity' },
    { title: '开始日期', dataIndex: 'plan_start_date', key: 'plan_start_date', render: (text) => dayjs(text).format('YYYY-MM-DD') },
    { title: '完成日期', dataIndex: 'plan_end_date', key: 'plan_end_date', render: (text) => dayjs(text).format('YYYY-MM-DD') },
    { title: '状态', dataIndex: 'status', key: 'status', render: (status) => <Tag color={statusMap[status]?.color}>{statusMap[status]?.text || status}</Tag> },
    { title: '备注', dataIndex: 'note', key: 'note' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        const isCompleted = record.status === 'completed';
        return (
          <Space size="small" style={{ marginLeft: -8 }}>
            <Button type="link" size="small" onClick={() => showModal(record)}>
              {isCompleted ? '查看' : '编辑'}
            </Button>
            {!isCompleted && (
              <Popconfirm title="确定删除此计划吗？" onConfirm={() => handleDelete(record.id)}>
                <Button type="link" size="small" danger>删除</Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>生产计划管理</Title>
        <Space>
          <Input.Search placeholder="搜索计划单号" onSearch={handleSearch} style={{ width: 200 }} allowClear />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>新增计划</Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={plans}
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        bordered
        size="small"
      />
      <Modal
        title={isViewMode ? '查看生产计划' : (editingRecord ? '编辑生产计划' : '新增生产计划')}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
        width={600}
        footer={isViewMode ? [<Button key="close" onClick={handleCancel}>关闭</Button>] : undefined}
      >
        <Form form={form} layout="vertical" name="plan_form">
          <Form.Item name="plan_no" label="计划单号">
            <Input disabled placeholder={editingRecord ? '' : '自动生成'} />
          </Form.Item>
          <Form.Item name="product_id" label="产品" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="children"
              disabled={isViewMode}
            >
              {products.map(p => <Option key={p.id} value={p.id}>{p.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="quantity" label="计划数量（包装）" rules={[{ required: true }]}>
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              disabled={isViewMode}
              placeholder="请输入包装数量"
            />
          </Form.Item>
          <Form.Item name="plan_start_date" label="计划开始日期" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabled={isViewMode} />
          </Form.Item>
          <Form.Item name="plan_end_date" label="计划完成日期" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabled={isViewMode} />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true }]}>
            <Select disabled={isViewMode}>
                {Object.entries(statusMap).map(([key, { text }]) => (
                    <Option key={key} value={key}>{text}</Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} disabled={isViewMode} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductionPlans;